<script setup>
import { ref, onBeforeUnmount, onMounted } from 'vue'
import eventBus from '@core/utils/eventBus'
import { setDomFontSize } from '@core/utils/index.js'
import { initUserStore, useUserStore, bootstrapNewPlayerSession } from '@core/store/user.js'
import PayView from '@components/iframeView/pay.vue'
import { initGameStore } from '@core/store/game.js'
import { initVipStore } from '@core/store/vip.js'
import { initConfigStore, useConfigStore } from '@core/store/config.js'
import { useSocket } from '@core/socket/core.js'
import { traceEvent } from '@core/utils/fbq.js'
import router from '@core/router/index.js'
import { showWelcome } from '@components/popup/welcome/index.js'
import { vantThemeVars } from '@core/config/theme-vars.js'
import { chatLogin, chatUnread } from '@utils/chat.js'
import { showGuideCashSuccess } from '@components/popup/guide/index.js'
import { enqueuePopup } from '@utils/popupQueue.js'
import { useRoute } from 'vue-router'
import { useGuideStore } from '@core/store/guide.js'

initConfigStore()
initUserStore()
initVipStore()
initGameStore()

const iframeMessage = event => {
  eventBus.emit('iframeMessage', event)
}

const socket = ref(null)

const userStore = useUserStore()
const configStore = useConfigStore()

const route = useRoute()

const loginSuccess = () => {
  if (!socket.value?.isOpen || !userStore.isLogin) return
  socket.value.sendMessage(
    JSON.stringify({
      cmd: 'login',
      data: userStore.user.token,
    }),
  )
  chatLogin(userStore.user)
  chatUnread()
}

function handleCashoutSuccess() {
  const local = localStorage.getItem('cashout_success')
  if (Number(local) !== userStore.user.id) {
    enqueuePopup(showGuideCashSuccess, {
      type: 'guideCashSuccess',
      priority: 1,
    })
    localStorage.setItem('cashout_success', userStore.user.id)
  }
}

// 测试代码
// const guideStore = useGuideStore()
// window.handleCashoutSuccessTest = () => {
//   enqueuePopup(showGuideCashSuccess, {
//     type: 'guideCashSuccess',
//     priority: 1,
//     // 需要在其他地方进行释放弹窗锁
//     lock: true,
//   })
//   guideStore.vipInit()
// }

const initWebSocket = () => {
  socket.value = useSocket()
  socket.value.subscribe('open', () => {
    console.info('WebSocket连接成功!')
    loginSuccess()
  })

  socket.value.subscribe('message', (result) => {
    try {
      const data = JSON.parse(result)
      const cmd = data.cmd

      console.log('cmd:', cmd, 'data:', data.data)

      if (cmd === 'roundid') {
        const result = JSON.parse(data.data)
        eventBus.emit('roundid', result.roundid)
      }

      if (cmd === 'beginer_limit') {
        eventBus.emit('beginer_limit')
      }

      if (cmd === 'cashout_success') {
        if (route.path === '/') {
          // 当前就是首页，直接执行
          handleCashoutSuccess()
        } else {
          // 不是首页，等待回到首页
          const stop = watch(
            () => route.path,
            (newPath) => {
              if (newPath === '/') {
                handleCashoutSuccess()
                stop() // 执行后停止监听
              }
            }
          )
        }
      }

      // 充值完成
      if (cmd === 'charge_success') {
        userStore.freshBalance()
        eventBus.emit('userPaySuccess')
        // result = { pay: Number, firstpay: Boolean, orderId: String }
        const result = JSON.parse(data.data)
        const event = result.firstpay ? 'first_pay_event' : 'routine_pay_event'
        const traceParams = {}
        if (result.pay >= 0.001) {
          traceParams.revenue = Number(result.pay.toFixed(2))
          traceParams.currency = 'USD'
        }
        if (result.orderId) {
          traceParams.orderId = result.orderId
        }
        console.log(traceParams)
        traceEvent(event, traceParams)

        if (result.firstpay && router.currentRoute.value.path !== '/game') {
          router.push('/')
        }
      }

      if (cmd === 'otherlogin') {
        userStore.logout()
      }

    } catch (e) {}
  })

  socket.value.subscribe('reconnect', (_socket) => {
    console.info('WebSocket断开重连:', _socket)
    socket.value = _socket
  })
}

onMounted(async () => {
  initWebSocket()
  window.addEventListener('resize', setDomFontSize)
  window.addEventListener('message', iframeMessage)
  eventBus.on('loginSuccess', loginSuccess)

  await bootstrapNewPlayerSession(userStore)
  if (!userStore.isLogin) {
    showWelcome()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', setDomFontSize)
  window.removeEventListener('message', iframeMessage)
  eventBus.off('loginSuccess', loginSuccess)
})
</script>

<template>
  <van-config-provider :theme-vars="vantThemeVars" class="app-root">
    <div class="router-view relative">
      <router-view v-slot="{ Component }">
        <KeepAlive>
          <component :is="Component"/>
        </KeepAlive>
      </router-view>
    </div>

    <pay-view/>
  </van-config-provider>
</template>

<style lang="scss">
.app-root {
  height: 100%;
}

.router-view {
  height: 100%;
  overflow-x: hidden;
}
</style>
